import { useRef, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMangaListInfinite, useMeta } from '../api/useManga'
import { useUpdateProfile } from '../api/useAuth'
import { useAuthStore } from '../store/authStore'
import MangaGrid from '../components/manga/MangaGrid'
import Adult18Modal from '../components/ui/Adult18Modal'

const SORT_OPTIONS = [
  { value: 'rating:desc', label: 'По рейтингу ↓' },
  { value: 'rating:asc',  label: 'По рейтингу ↑' },
  { value: 'date:desc',   label: 'По дате (новые)' },
  { value: 'date:asc',    label: 'По дате (старые)' },
  { value: 'popular:desc',label: 'Самые популярные' },
  { value: 'title:asc',   label: 'По алфавиту А→Я' },
  { value: 'title:desc',  label: 'По алфавиту Я→А' },
]

const RATING_OPTIONS = [
  { value: '', label: 'Любой рейтинг' },
  { value: '6', label: '6★ и выше' },
  { value: '7', label: '7★ и выше' },
  { value: '8', label: '8★ и выше' },
  { value: '9', label: '9★ и выше' },
]

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showAllGenres, setShowAllGenres] = useState(false)
  const sentinelRef = useRef(null)
  const { data: meta } = useMeta()
  const GENRES = meta?.genres ?? []
  const STATUS_OPTIONS = [
    { value: '', label: 'Любой статус' },
    ...(meta?.statuses ?? []).map((s) => ({ value: s.code, label: s.name })),
  ]
  const TYPE_OPTIONS = [
    { value: '', label: 'Любой тип' },
    ...(meta?.types ?? []).map((t) => ({ value: t.code, label: t.name })),
  ]

  const { user, isAuth, setUser } = useAuthStore()
  const { mutate: updateProfile } = useUpdateProfile()
  const [localAdult, setLocalAdult] = useState(() => localStorage.getItem('manga_show_adult') === 'true')
  const showAdult = isAuth ? (user?.show_adult ?? false) : localAdult
  const [showAdult18Modal, setShowAdult18Modal] = useState(false)

  const applyToggleAdult = () => {
    if (isAuth) {
      const next = !user?.show_adult
      setUser({ ...user, show_adult: next })
      updateProfile({ show_adult: next }, {
        onError: () => setUser({ ...user, show_adult: !next }),
      })
    } else {
      const next = !localAdult
      setLocalAdult(next)
      localStorage.setItem('manga_show_adult', String(next))
    }
  }

  const toggleAdult = () => {
    if (!showAdult) {
      setShowAdult18Modal(true)
    } else {
      applyToggleAdult()
    }
  }

  const genre = searchParams.get('genre') || ''
  const status = searchParams.get('status') || ''
  const type = searchParams.get('type') || ''
  const sortParam = searchParams.get('sort') || 'rating:desc'
  const minRating = searchParams.get('min_rating') || ''
  const selectedGenres = genre ? genre.split(',').filter(Boolean) : []
  const [sortKey, sortOrder] = sortParam.includes(':') ? sortParam.split(':') : [sortParam, 'desc']

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useMangaListInfinite({
    genre: genre || undefined,
    status: status || undefined,
    type: type || undefined,
    sort: sortKey,
    min_rating: minRating || undefined,
    order: sortOrder,
    show_adult: showAdult || undefined,
  })

  const items = data?.pages.flatMap((p) => p.items || p) || []
  const total = data?.pages[0]?.total || 0

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage() },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const updateParam = (key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value) next.set(key, value)
      else next.delete(key)
      return next
    })
  }

  const toggleGenre = (slug) => {
    const next = selectedGenres.includes(slug)
      ? selectedGenres.filter((s) => s !== slug)
      : [...selectedGenres, slug]
    updateParam('genre', next.join(','))
  }

  return (
    <>
    <Adult18Modal
      isOpen={showAdult18Modal}
      onClose={() => setShowAdult18Modal(false)}
      onConfirm={() => { setShowAdult18Modal(false); applyToggleAdult() }}
    />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <title>Каталог манги — {import.meta.env.VITE_SITE_NAME}</title>
      <meta name="description" content="Полный каталог манги на русском языке. Фильтрация по жанрам, типу, рейтингу и статусу." />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={import.meta.env.VITE_SITE_NAME} />
      <meta property="og:title" content={`Каталог манги — ${import.meta.env.VITE_SITE_NAME}`} />
      <meta property="og:description" content="Полный каталог манги на русском языке. Фильтрация по жанрам, типу, рейтингу и статусу." />
      <link rel="canonical" href={`${window.location.origin}/catalog`} />
      <h1 className="text-2xl mb-6">Каталог</h1>

      <div className="rounded-2xl p-4 mb-6 flex flex-col gap-4 bg-surface border border-line">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <FilterSelect value={status} onChange={(v) => updateParam('status', v)} options={STATUS_OPTIONS} label="Статус" />
          <FilterSelect value={type} onChange={(v) => updateParam('type', v)} options={TYPE_OPTIONS} label="Тип" />
          <FilterSelect value={minRating} onChange={(v) => updateParam('min_rating', v)} options={RATING_OPTIONS} label="Рейтинг" />
          <FilterSelect value={sortParam} onChange={(v) => updateParam('sort', v)} options={SORT_OPTIONS} label="Сортировка" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted">
              Жанры {selectedGenres.length > 0 && <span className="text-accent">· {selectedGenres.length} выбрано</span>}
            </span>
            <div className="flex items-center gap-2">
              {(selectedGenres.length > 0 || status || type || minRating) && (
                <button onClick={() => setSearchParams({})}
                  className="text-xs text-muted hover:text-red-400 transition-colors">
                  Сбросить
                </button>
              )}
              <button onClick={toggleAdult}
                className={`cursor-pointer text-xs px-2 py-0.5 rounded-md font-semibold border transition-colors
                  ${showAdult
                    ? 'bg-red-400/15 text-red-400 border-red-400/30'
                    : 'bg-surface2 text-muted border-line hover:border-accent'
                  }`}>
                18+
              </button>
              <button onClick={() => setShowAllGenres((v) => !v)}
                className="text-xs text-muted hover:text-fg transition-colors">
                {showAllGenres ? 'Свернуть ↑' : 'Все жанры ↓'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(showAllGenres ? GENRES : GENRES.filter((g, i) => i < 10 || selectedGenres.includes(g.code))).map((g) => {
              const active = selectedGenres.includes(g.code)
              return (
                <button key={g.code} onClick={() => toggleGenre(g.code)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all border
                    ${active
                      ? 'bg-accent text-on-accent border-accent'
                      : 'bg-surface2 text-muted border-line hover:border-accent'
                    }`}
                >
                  {g.name}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {total > 0 && <p className="text-sm mb-4 text-muted">Найдено: {total}</p>}

      <MangaGrid items={items} loading={isLoading} />

      <div ref={sentinelRef} className="h-1" />

      {isFetchingNextPage && (
        <div className="flex justify-center py-6">
          <div className="w-6 h-6 rounded-full border-2 animate-spin"
            style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }} />
        </div>
      )}
    </div>
    </>
  )
}

function FilterSelect({ value, onChange, options, label }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-muted">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 rounded-lg text-sm outline-none transition-colors bg-surface2 border border-line text-fg">
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: 'var(--color-surface)' }}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}
